import { useState } from "react";
import useTopGainers from "../hooks/useTopGainers";
import StockCard from "./StockCard";
import Pagination from "./Pagination";
import { longDate, sessionLabel } from "../utils/format";

const PAGE_SIZE = 12;

export default function TopGainers({ active, onSelect }) {
  const { gainers, loading, error, refresh, refreshing, scrapeDate } = useTopGainers(active);
  const [page, setPage] = useState(1);
  const [showIncomplete, setShowIncomplete] = useState(false);
  const [staleSession, setStaleSession] = useState(null);

  // Parse the ISO scrape date as local (avoid the UTC off-by-one).
  const dateLabel = scrapeDate ? longDate(new Date(`${scrapeDate}T00:00:00`)) : "Today";

  const pageCount = Math.max(1, Math.ceil(gainers.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const paginated = gainers.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <>
      <div className="masthead">
        <div className="dateline">
          <span className="kicker">Market Movers</span>
          <span className="kicker dateline-sep">·</span>
          <span className="kicker">{dateLabel}</span>
        </div>
        <h1>Top gainers today</h1>
        <p className="lede">
          The day's biggest percentage movers. Select any stock to open its full
          chart and prediction.
        </p>
      </div>

      <div className="gainers-toolbar">
        <button
          className={`btn-icon${refreshing || loading ? " spinning" : ""}`}
          onClick={refresh}
          disabled={refreshing || loading}
          title="Refresh gainers"
          aria-label="Refresh gainers"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M20 11a8 8 0 10-1.5 5M20 5v6h-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <label className="incomplete-toggle">
          <input
            type="checkbox"
            checked={showIncomplete}
            onChange={(e) => setShowIncomplete(e.target.checked)}
          />
          Show limited-data charts
        </label>
        {!loading && gainers.length > 0 && (
          <span className="gainers-count">{gainers.length} movers</span>
        )}
      </div>

      {staleSession && (
        <div className="market-note">
          Market closed — intraday charts show the last session ({sessionLabel(staleSession)}).
        </div>
      )}

      {error && !loading ? (
        <div className="panel panel-pad">
          <div className="state state--error">
            {error}
            <button className="retry" onClick={refresh}>
              Retry
            </button>
          </div>
        </div>
      ) : loading ? (
        <div className="stock-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="stock-card">
              <span className="skeleton sk-line" style={{ width: "60%" }} />
              <span className="skeleton sk-line" style={{ width: "40%" }} />
              <span className="skeleton stock-chart-sk" />
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="stock-grid fade-in" key={safePage}>
            {paginated.map((stock) => (
              <StockCard
                key={stock.ticker}
                stock={stock}
                showIncomplete={showIncomplete}
                onSelect={onSelect}
                onStale={(d) => setStaleSession((prev) => prev || d)}
              />
            ))}
          </div>
          <Pagination
            total={gainers.length}
            page={safePage}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </>
      )}
    </>
  );
}
